import { ActionTree, ActionContext } from 'vuex'

import { IRecurly, IProductExtended, IPlan, IRootState } from '@/@types'
import CONSTANTS from '@/constants'
import { ensureRecurlyReady } from '@/helpers/thirdPartyLoaders'
import { isSubscriptionPlan } from '@/helpers/productClassification'

const { PLANS, BLACK_FRIDAY_PRODUCT_BUNDLES } = CONSTANTS
const { INE_BLACK_FRIDAY_BUNDLE_THREE } = BLACK_FRIDAY_PRODUCT_BUNDLES

const handleError = (tag: string, error: any) => {
  // TODO manage errors without console.error
  // console.error(`handleError [${tag}]`, error)
}

const hasRecurlyPricingInstance = (pricing: any) =>
  Boolean(
    pricing &&
      typeof pricing.subscription === 'function' &&
      typeof pricing.adjustment === 'function'
  )

const actions: ActionTree<IRecurly, IRootState> = {
  createRecurlyInstance: async (context: ActionContext<IRecurly, IRootState>) => {
    try {
      await ensureRecurlyReady(process.env.VUE_APP_RECURLY_KEY)
      if (hasRecurlyPricingInstance(context.state.recurlyPricing)) {
        return context.state.recurlyPricing
      }
      context.commit('RECURLY_INSTANCE_UPDATED')
    } catch (error: any) {
      handleError('createRecurlyInstance', error)
    }
    return context.state.recurlyPricing
  },
  setRecurlyToken: (context: ActionContext<IRecurly, IRootState>, payload) => {
    context.commit('RECURLY_TOKEN_UPDATED', payload)
  },
  setRecurlyTokenActionId: (context: ActionContext<IRecurly, IRootState>, payload) => {
    context.commit('RECURLY_ACTION_TOKEN_ID_UPDATED', payload)
  },
  setRecurlyPricing: (context: ActionContext<IRecurly, IRootState>, recurlyPricing: object) => {
    context.commit('RECURLY_PRICING_UPDATED', recurlyPricing)
  },
  setProductRecurlyItem: (
    context: ActionContext<IRecurly, IRootState>,
    payload: { productRecurlyItem: object; value: boolean }
  ) => {
    context.commit('RECURLY_PRODUCT_RECURLY_ITEM_UPDATED', payload)
  },
  calculateRecurlyPricing: async (
    context: ActionContext<IRecurly, IRootState>,
    item: IProductExtended
  ) => {
    let recurlyPricing = context.getters.getRecurlyPricing || null
    if (!hasRecurlyPricingInstance(recurlyPricing)) {
      await context.dispatch('createRecurlyInstance')
      recurlyPricing = context.getters.getRecurlyPricing || null
    }
    if (!hasRecurlyPricingInstance(recurlyPricing)) {
      return
    }

    if (isSubscriptionPlan(item)) {
      context.dispatch('calculatePricingForPlans', item)
    } else {
      if (
        !recurlyPricing.items?.adjustments?.find(
          (recurlyItem: any) => recurlyItem.id === item.merchant_id
        )
      ) {
        context.dispatch('addSingleProduct', item)
      }
    }
  },
  calculatePricingForPlans: (context: ActionContext<IRecurly, IRootState>, item: any) => {
    if (item.merchant_id === CONSTANTS.PLANS.SKILL_DIVE_ANNUAL) {
      if (!item.recurlyItem || item.recurlySDLACounter !== item.addOnsOnCart.length) {
        item.recurlySDLACounter = item.addOnsOnCart.length
        context.dispatch('createSubscription', item)
      }
    } else {
      if (!item.recurlyItem) {
        context.dispatch('createSubscription', item)
      }
    }
  },
  createSubscription: async (context: ActionContext<IRecurly, IRootState>, plan: IPlan) => {
    let recurlyPricing = context.getters.getRecurlyPricing
    if (!hasRecurlyPricingInstance(recurlyPricing)) {
      await context.dispatch('createRecurlyInstance')
      recurlyPricing = context.getters.getRecurlyPricing
    }
    if (!hasRecurlyPricingInstance(recurlyPricing)) {
      return
    }
    const paymentEntries = context.rootGetters['subscriptions/getPaymentEntries']
    const { country, zip: postal_code } = paymentEntries
    let subscription: any = {}

    if (plan.merchant_id === INE_BLACK_FRIDAY_BUNDLE_THREE) {
      subscription = window.recurly.Pricing.Subscription().plan(PLANS.ANNUAL)
    } else if (plan.merchant_id === PLANS.SKILL_DIVE_ANNUAL) {
      subscription = window.recurly.Pricing.Subscription().plan(plan.merchant_id)
      plan.addOnsOnCart?.forEach(SDAddOn => subscription.addon(SDAddOn.merchant_id))
    } else {
      subscription = window.recurly.Pricing.Subscription().plan(plan.merchant_id)
    }

    context.commit('RECURLY_PRODUCT_RECURLY_ITEM_UPDATED', {
      productRecurlyItem: plan,
      value: true,
    })

    await recurlyPricing
      .subscription(
        subscription
          .catch((error: any) => handleError('createSubscription:subscription', error))
          .done()
      )
      .address({ country, postal_code })
      .catch((error: any) => handleError('createSubscription:address', error))

    context.dispatch('setRecurlyPricing', recurlyPricing)
  },
  addSingleProduct: async (context: ActionContext<IRecurly, IRootState>, item: IPlan) => {
    let recurlyPricing = context.getters.getRecurlyPricing
    if (!hasRecurlyPricingInstance(recurlyPricing)) {
      await context.dispatch('createRecurlyInstance')
      recurlyPricing = context.getters.getRecurlyPricing
    }
    if (!hasRecurlyPricingInstance(recurlyPricing)) {
      return
    }
    const paymentEntries = context.rootGetters['subscriptions/getPaymentEntries']
    const { country, zip: postal_code } = paymentEntries

    await recurlyPricing
      .adjustment({
        id: item.merchant_id,
        itemCode: item.merchant_id,
        quantity: 1,
        taxExempt: false,
      })
      .address({ country, postal_code })
      .catch((error: any) => handleError('addSingleProduct:recurlyPricing', error))

    context.dispatch('setRecurlyPricing', recurlyPricing)
  },
  setRecurlyCoupon: (_context: ActionContext<IRecurly, IRootState>, _coupon: string) => {
    // Coupon validation is sourced from subscriptions API.
    // Keep this action as a no-op for backward compatibility with existing dispatch calls.
    return Promise.resolve()
  },
}

export default actions
